import React from "react";
import { HeartHandshake, Activity, Users, Compass, Wind, Repeat } from "lucide-react";
import ApplicationTemplate from "@/components/applications/ApplicationTemplate";

const Recovery = () => (
  <ApplicationTemplate
    pageKey="recovery"
    breadcrumbLabel="Recovery"
    overline="Application · Recovery"
    headline="A nervous system that learned to depend on a substance"
    headlineAccent="can learn something else."
    lead="Recovery is not a failure of character. It is the intelligent output of a physiology that has adapted to substance-mediated regulation. Adaptive Physiology works with that reality — not against it."
    loopHighlight="new-adaptation"
    challenge={{
      title: "Dependency is a physiological adaptation, not a moral one.",
      paragraphs: [
        "Repeated substance exposure trains the reward, stress, and homeostatic systems to expect and require the input. Dopamine sensitivity shifts. Autonomic tone narrows. The state that once required the substance now produces cravings for it.",
        "This is the nervous system doing exactly what it was trained to do — respond efficiently to a repeated input. Recovery is the process of giving that same system a different set of inputs.",
      ],
    }}
    traditionalGap={{
      title: "Most recovery models address one layer at a time.",
      paragraphs: [
        "12-step frameworks address community and identity. Cognitive approaches address thought patterns. Medication addresses receptor chemistry. Each layer is valuable, and none of them are wrong.",
        "What is often missing is direct, sustained intervention into the physiological state itself — the autonomic, endocrine, and neuroplastic system that keeps generating the pattern between sessions. Adaptive Physiology adds that layer.",
      ],
    }}
    perspective={{
      title: "Recovery is retraining the physiology that has adapted to the substance.",
      paragraphs: [
        "The goal is not to suppress the state that seeks the substance. It is to build a new physiological default from which that pursuit becomes less compelling.",
        "Movement, structured input, rhythmic recovery, environmental design, and behavioral reinforcement become the new signals. The nervous system does what it does best — it adapts to what it receives, consistently.",
      ],
    }}
    frameworkApplication={[
      {
        title: "Baseline the recovery state",
        body: "Autonomic tone, sleep architecture, resting HR/HRV, craving cadence, and current movement capacity are captured as the starting signal.",
      },
      {
        title: "Prescribe the retraining stimulus",
        body: "Precisely dosed aerobic and resistance loading, environmental structure, and paced daily rhythm — matched to the nervous system’s current tolerance.",
      },
      {
        title: "Reinforce through daily rhythm",
        body: "Repetition and accountability ensure the new signal lands consistently — the condition under which the nervous system actually adapts.",
      },
      {
        title: "Retrain the default state",
        body: "The set-point that seeks substance-mediated regulation begins to shift. New behavior emerges as the output of a different physiological state.",
      },
    ]}
    capacities={[
      { icon: HeartHandshake, label: "Autonomic regulation", body: "Capacity to return to a settled state without external input." },
      { icon: Activity, label: "Adaptive stress response", body: "A stress response that engages and releases on cue." },
      { icon: Repeat, label: "Consistency capacity", body: "The physiological ability to sustain new routines through friction." },
      { icon: Compass, label: "Behavioral flexibility", body: "Room between trigger and response — space where choice becomes possible." },
      { icon: Wind, label: "Recovery velocity", body: "Faster return to baseline after physical, emotional, or social load." },
      { icon: Users, label: "Meaningful engagement", body: "Physiological availability for connection, recreation, and purpose." },
    ]}
  />
);
export default Recovery;
